/*****************************************************
Naam: CustomerTrackingMap.jsx
Versie: 3.0.0 - AUTO-ZOOM + KLEURPICKER
Datum: 2026-03-19
CHANGELOG v3.0.0:
- Auto-zoom: kaart past zich aan op alle zichtbare routes
- Kleurpicker: klik op kleurvak in zendingskaart om kleur te kiezen
- Kleurvoorkeur opgeslagen in localStorage per aanvraag
- FitBounds ook op laad/los coördinaten via geocoding
******************************************************/

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useLanguage } from '../../LanguageContext';
import { MapContainer, TileLayer, Marker, Popup, Polyline, CircleMarker, Tooltip, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { Filter, MapPin } from 'lucide-react';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png'
});

// Coördinaten van bekende steden voor auto-zoom op volledige route
const STAD_COORDS = {
  'Eindhoven':[51.4416,5.4697],'Rotterdam':[51.9244,4.4777],
  'Enschede':[52.2215,6.8937],'Oldenzaal':[52.3086,6.9289],
  'Parijs':[48.8566,2.3522],'Berlijn':[52.5200,13.4050],
  'Frankfurt':[50.1109,8.6821],'München':[48.1351,11.5820],
  'Hamburg':[53.5753,10.0153],'Milaan':[45.4654,9.1859],
  'Madrid':[40.4168,-3.7038],'Brussel':[50.8503,4.3517],
  'Antwerpen':[51.2194,4.4025],'Zürich':[47.3769,8.5417],
  'Wenen':[48.2082,16.3738],'Warschau':[52.2297,21.0122],
  'Poznan':[52.4064,16.9252],'Praag':[50.0755,14.4378],
  'Boedapest':[47.4979,19.0402],'Kopenhagen':[55.6761,12.5683],
  'Oslo':[59.9139,10.7522],'Stockholm':[59.3293,18.0686],
  'Lissabon':[38.7169,-9.1399],'Lille':[50.6292,3.0573],
};

// AutoFitBounds - eenmalig zoomen op GPS-routes + eindbestemmingen
const AutoFitBounds = ({ trackerPositions, selectedTrackers, aanvragen }) => {
  const map = useMap();
  const heeftGezoomd = useRef(false);

  const aantalSelected = aanvragen.filter(function(a) {
    return selectedTrackers.has(a.AanvraagID) && a.TrackerID;
  }).length;
  const aantalMetPositie = aanvragen.filter(function(a) {
    return selectedTrackers.has(a.AanvraagID) && a.TrackerID && trackerPositions[a.TrackerID];
  }).length;

  useEffect(() => {
    // Zoom eenmalig zodra alle GPS-data binnen is
    if (heeftGezoomd.current) return;
    if (aantalSelected === 0 || aantalMetPositie < aantalSelected) return;

    const allPoints = [];
    aanvragen.forEach(function(aanvraag) {
      if (!selectedTrackers.has(aanvraag.AanvraagID) || !aanvraag.TrackerID) return;

      // GPS history (0-65% van route)
      var pos = trackerPositions[aanvraag.TrackerID];
      if (pos) {
        if (pos.current) allPoints.push([pos.current.latitude, pos.current.longitude]);
        if (pos.history) pos.history.forEach(function(p) { allPoints.push(p); });
      }

      // Voeg laad- én losplaats toe zodat volledige route in beeld is
      var laad = STAD_COORDS[aanvraag.LaadPlaats];
      var los  = STAD_COORDS[aanvraag.LosPlaats];
      if (laad) allPoints.push(laad);
      if (los)  allPoints.push(los);
    });

    if (allPoints.length === 0) return;

    heeftGezoomd.current = true;
    // invalidateSize zorgt dat Leaflet de echte schermafmetingen gebruikt
    // setTimeout geeft de browser tijd om de layout te voltooien
    setTimeout(function() {
      map.invalidateSize({ reset: true });
      map.fitBounds(L.latLngBounds(allPoints), {
        padding: [40, 40],
        maxZoom: 12,
        animate: false
      });
    }, 300);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aantalMetPositie, aantalSelected]);

  return null;
};

const MapRefSetter = ({ mapRef }) => {
  const map = useMap();
  useEffect(() => { mapRef.current = map; }, [map, mapRef]);
  return null;
};

const InvalidateSize = () => {
  const map = useMap();
  useEffect(() => {
    const timer = setTimeout(() => map.invalidateSize(), 100);
    const handleResize = () => map.invalidateSize();
    window.addEventListener('resize', handleResize);
    return () => { clearTimeout(timer); window.removeEventListener('resize', handleResize); };
  }, [map]);
  return null;
};

// Reset knop
const ResetControl = ({ trackerPositions, selectedTrackers, filteredAanvragen }) => {
  const { t } = useLanguage();
  const map = useMap();
  return (
    <div className="leaflet-top leaflet-left" style={{marginTop:'80px'}}>
      <div className="leaflet-control leaflet-bar">
        <a href="#" title={t('zendingen')}
          onClick={function(e){
            e.preventDefault();
            var allPoints = [];
            filteredAanvragen.forEach(function(a) {
              if (!selectedTrackers.has(a.AanvraagID)) return;
              // GPS punten
              var pos = trackerPositions[a.TrackerID];
              if (pos) {
                if (pos.current) allPoints.push([pos.current.latitude, pos.current.longitude]);
                if (pos.history) pos.history.forEach(function(p) { allPoints.push(p); });
              }
              // Laad en los stad coördinaten
              var laadCoord = STAD_COORDS[a.LaadPlaats];
              var losCoord  = STAD_COORDS[a.LosPlaats];
              if (laadCoord) allPoints.push(laadCoord);
              if (losCoord)  allPoints.push(losCoord);
            });
            if (allPoints.length > 0) {
              map.fitBounds(L.latLngBounds(allPoints), {
                padding: [30, 30],
                maxZoom: 12,
                animate: false
              });
            }
          }}
          style={{display:'flex',alignItems:'center',justifyContent:'center',width:30,height:30,fontSize:15,color:'#333',background:'white',textDecoration:'none',fontWeight:'700'}}>⌖</a>
      </div>
    </div>
  );
};

// v3.6: min/max van tracker coördinaten = exacte kaartgrenzen

var createColoredIcon = function(color) {
  return L.divIcon({
    className: '',
    html: '<div style="background:' + color + ';width:24px;height:24px;border-radius:50%;border:3px solid white;box-shadow:0 2px 6px rgba(0,0,0,0.35);"></div>',
    iconSize: [24, 24],
    iconAnchor: [12, 12],
    popupAnchor: [0, -14]
  });
};

var createEndpointIcon = function(color, type) {
  const symbol = type === 'load' ? '📦' : '🏁';
  return L.divIcon({
    className: '',
    html: `<div style="background:white;border:2px solid ${color};border-radius:4px;padding:2px 4px;font-size:14px;box-shadow:0 2px 6px rgba(0,0,0,0.2);white-space:nowrap;">${symbol}</div>`,
    iconSize: [28, 28],
    iconAnchor: [14, 14],
  });
};

// Geocode een adres naar coördinaten
const geocodeCache = {};
const geocodeAdres = async (plaats, land) => {
  if (!plaats) return null;
  const key = `${plaats},${land}`;
  if (geocodeCache[key]) return geocodeCache[key];
  try {
    const q = encodeURIComponent(`${plaats}, ${land || ''}`);
    const res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${q}&limit=1`, {
      headers: { 'User-Agent': 'SendWell/3.0' }
    });
    const data = await res.json();
    if (data && data[0]) {
      const coord = [parseFloat(data[0].lat), parseFloat(data[0].lon)];
      geocodeCache[key] = coord;
      return coord;
    }
  } catch(e) {}
  return null;
};

// Tooltip inhoud
const RouteTooltipContent = ({ pos }) => {
  const { t } = useLanguage();
  const [address, setAddress] = useState(null);
  useEffect(() => {
    if (!pos) return;
    fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${pos.latitude}&lon=${pos.longitude}&zoom=16&addressdetails=1`, {
      headers: { 'User-Agent': 'SendWell/3.0' }
    }).then(r => r.json()).then(d => {
      if (d && d.address) setAddress([d.address.road, d.address.city || d.address.town || d.address.village, d.address.country].filter(Boolean).join(', '));
      else setAddress(t('onbekend') || 'Onbekend');
    }).catch(() => setAddress(t('onbekend') || 'Onbekend'));
  }, [pos]);
  if (!pos) return null;
  return (
    <div style={{ fontSize: '11px', minWidth: '150px' }}>
      <div style={{ fontWeight: '600', marginBottom: '2px' }}>
        {new Date(pos.timestamp).toLocaleString('nl-NL', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
      </div>
      <div style={{ color: '#555' }}>{address === null ? t('laden') : address}</div>
    </div>
  );
};

// ==================== LINE COLORS ====================
const defaultColorPalette = [
  '#7c3aed', '#2563eb', '#059669', '#dc2626',
  '#d97706', '#0891b2', '#7c3aed', '#be185d',
];

const getDefaultColorForId = (id) => {
  let hash = 0;
  for (let i = 0; i < id.length; i++) hash = id.charCodeAt(i) + ((hash << 5) - hash);
  return defaultColorPalette[Math.abs(hash) % defaultColorPalette.length];
};

const KLEUR_OPTIES = [
  '#7c3aed', '#2563eb', '#059669', '#dc2626',
  '#d97706', '#0891b2', '#be185d', '#000000',
  '#16a34a', '#9333ea', '#ea580c', '#0284c7',
];

// Laad opgeslagen kleuren
const loadSavedColors = () => {
  try { return JSON.parse(localStorage.getItem('sw_zending_kleuren') || '{}'); } catch(e) { return {}; }
};
const saveColors = (colors) => {
  try { localStorage.setItem('sw_zending_kleuren', JSON.stringify(colors)); } catch(e) {}
};

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('nl-NL', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const formatTimestamp = (timestamp) => {
  if (!timestamp) return '-';
  var diffMins = Math.floor((new Date() - new Date(timestamp)) / 60000);
  if (diffMins < 1) return 'Zojuist';
  if (diffMins < 60) return diffMins + ' min geleden';
  if (diffMins < 1440) return Math.floor(diffMins / 60) + ' uur geleden';
  return new Date(timestamp).toLocaleString('nl-NL', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
};

const isLate = (losDatum) => {
  if (!losDatum) return false;
  var today = new Date(); today.setHours(0, 0, 0, 0);
  var los = new Date(losDatum); los.setHours(0, 0, 0, 0);
  return today > los;
};

const DetailRow = ({ label, value, color }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px', fontSize: '12px' }}>
    <span style={{ color: '#888', minWidth: '80px' }}>{label}</span>
    <span style={{ color: color || '#333', fontWeight: color ? '600' : '400', textAlign: 'right', wordBreak: 'break-word', maxWidth: '170px' }}>{value || '-'}</span>
  </div>
);

// ==================== KLEURPICKER ====================
const KleurPicker = ({ aanvraagId, currentColor, onColorChange }) => {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ position: 'relative' }}>
      <div
        onClick={(e) => { e.stopPropagation(); setOpen(!open); }}
        title="Klik om kleur te wijzigen"
        style={{
          width: '18px', height: '18px', borderRadius: '4px', background: currentColor,
          cursor: 'pointer', border: '2px solid rgba(255,255,255,0.8)',
          boxShadow: '0 1px 4px rgba(0,0,0,0.3)', flexShrink: 0
        }}
      />
      {open && (
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            position: 'absolute', top: '22px', right: 0, zIndex: 9999,
            background: 'white', border: '1px solid #e5e7eb', borderRadius: '8px',
            padding: '8px', boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
            display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '5px', width: '108px'
          }}
        >
          {KLEUR_OPTIES.map(color => (
            <div
              key={color}
              onClick={() => { onColorChange(aanvraagId, color); setOpen(false); }}
              style={{
                width: '20px', height: '20px', borderRadius: '4px', background: color,
                cursor: 'pointer', border: color === currentColor ? '2px solid #1f2937' : '2px solid transparent',
                boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// ==================== TRACKER CARD ====================
const TrackerCard = ({ aanvraag, position, isSelected, isExpanded, onToggleSelect, onToggleExpand, lineColor, onColorChange }) => {
  const { t } = useLanguage();
  var hasPosition = !!(position && position.current);
  var late = isLate(aanvraag.LosDatum);

  return (
    <div style={{
      background: isSelected ? '#f5f0ff' : '#fff',
      border: '2px solid ' + (isSelected ? '#7c3aed' : '#e8e8e8'),
      borderRadius: '10px', marginBottom: '10px', overflow: 'hidden',
      transition: 'all 0.2s',
      boxShadow: isSelected ? '0 2px 8px rgba(124,58,237,0.15)' : '0 1px 3px rgba(0,0,0,0.05)'
    }}>
      <div style={{ padding: '12px 14px', cursor: 'pointer' }} onClick={onToggleSelect}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
            <input type="checkbox" checked={isSelected} onChange={onToggleSelect}
              onClick={function(e) { e.stopPropagation(); }}
              style={{ width: '16px', height: '16px', cursor: 'pointer', flexShrink: 0, accentColor: '#7c3aed' }} />
            <div style={{ width: '10px', height: '10px', borderRadius: '50%',
              background: hasPosition ? '#22c55e' : '#f59e0b', flexShrink: 0 }} />
            <span style={{ fontWeight: '700', fontSize: '14px', color: '#7c3aed' }}>
              {aanvraag.AanvraagID}
            </span>
            <KleurPicker
              aanvraagId={aanvraag.AanvraagID}
              currentColor={lineColor}
              onColorChange={onColorChange}
            />
          </div>
          <span style={{
            padding: '3px 8px', borderRadius: '10px', fontSize: '10px', fontWeight: '600',
            background: hasPosition ? '#dcfce7' : '#fef3c7',
            color: hasPosition ? '#166534' : '#92400e', letterSpacing: '0.5px'
          }}>
            {hasPosition ? t('live') : t('geen_gps') || 'GEEN DATA'}
          </span>
        </div>

        <div style={{ fontSize: '12px', color: '#555', display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '2px' }}>
          <span>{aanvraag.LaadPlaats || '-'}</span>
          <span style={{ color: '#bbb' }}>&rarr;</span>
          <span>{aanvraag.LosPlaats || '-'}</span>
        </div>
        <div style={{ fontSize: '11px', color: '#888', display: 'flex', gap: '8px' }}>
          <span>{formatDate(aanvraag.LaadDatum)}</span>
          <span style={{ color: '#bbb' }}>&rarr;</span>
          <span style={{ color: late ? '#dc2626' : '#888', fontWeight: late ? '700' : '400' }}>
            {formatDate(aanvraag.LosDatum)}
          </span>
          {late && <span style={{ fontSize: '11px', color: '#dc2626', fontWeight: '600' }}>{t('te_laat') || 'Te laat!'}</span>}
        </div>
      </div>

      {hasPosition && (
        <div style={{ padding: '0 14px 8px', borderTop: '1px solid #f0f0f0', paddingTop: '8px', background: 'rgba(124,58,237,0.03)' }}>
          <div style={{ fontSize: '11px', color: '#888', display: 'flex', gap: '5px', alignItems: 'center' }}>
            <span>🕒</span>
            <span>{t('laatste_update')}: {formatTimestamp(position.current.timestamp)}</span>
          </div>
        </div>
      )}

      <div
        onClick={(e) => { e.stopPropagation(); onToggleExpand(); }}
        style={{ padding: '6px 14px', background: '#f9f9f9', borderTop: '1px solid #f0f0f0',
          fontSize: '11px', color: '#888', cursor: 'pointer', display: 'flex', justifyContent: 'center',
          alignItems: 'center', gap: '4px'
        }}
      >
        {isExpanded ? t('minder_info') : t('meer_info')}
      </div>

      {isExpanded && (
        <div style={{ padding: '10px 14px', background: '#fafafa', borderTop: '1px solid #f0f0f0' }}>

          {/* Zending details */}
          <div style={{ fontSize: '11px', fontWeight: '700', color: '#7c3aed', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            {t('zendingdossier')}
          </div>
          <DetailRow label="Klant" value={aanvraag.Klant} />
          <DetailRow label="Status" value={aanvraag.Status} color="#7c3aed" />
          <DetailRow label="Laadadres" value={[aanvraag.LaadAdres, aanvraag.LaadPostcode, aanvraag.LaadPlaats].filter(Boolean).join(', ')} />
          <DetailRow label="Losadres" value={[aanvraag.LosAdres, aanvraag.LosPostcode, aanvraag.LosPlaats].filter(Boolean).join(', ')} />
          {aanvraag.GekozenVervoerder && <DetailRow label="Vervoerder" value={aanvraag.GekozenVervoerder} />}

          {/* Tracker info */}
          <div style={{ fontSize: '11px', fontWeight: '700', color: '#7c3aed', margin: '8px 0 6px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            Tracker info
          </div>
          <DetailRow label="Tracker" value={aanvraag.TrackerID} />
          {hasPosition && position.current ? (
            <>
              <DetailRow
                label={t('laatste_update')}
                value={position.current.timestamp
                  ? new Date(position.current.timestamp).toLocaleString('nl-NL', { day:'2-digit', month:'2-digit', hour:'2-digit', minute:'2-digit' })
                  : position.current.time
                    ? new Date(position.current.time).toLocaleString('nl-NL', { day:'2-digit', month:'2-digit', hour:'2-digit', minute:'2-digit' })
                    : '-'}
              />
              <DetailRow label="Positie" value={`${Number(position.current.latitude).toFixed(4)}, ${Number(position.current.longitude).toFixed(4)}`} />
              <DetailRow label="GPS punten" value={`${position.history ? position.history.length : 0} geregistreerd`} />
            </>
          ) : (
            <DetailRow label="Status" value="Geen tracker data ontvangen" />
          )}

          {/* Goederen */}
          {aanvraag.Goederen && aanvraag.Goederen.length > 0 && (
            <>
              <div style={{ fontSize: '11px', fontWeight: '700', color: '#7c3aed', margin: '8px 0 6px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Goederen
              </div>
              {aanvraag.Goederen.filter(g => (g.Soort || g.soort || g.Omschrijving || g.omschrijving)).map((g, i) => {
                const soort = g.Soort || g.soort || '';
                const omschr = g.Omschrijving || g.omschrijving || '';
                const aantal = g.Aantal || g.aantal || '';
                const gewicht = g.Gewicht || g.gewicht || '';
                const afm = (g.Lengte || g.lengte) ? `${g.Lengte||g.lengte}×${g.Breedte||g.breedte}×${g.Hoogte||g.hoogte}m` : '';
                return (
                  <div key={i} style={{ fontSize: '12px', color: '#555', marginBottom: '4px', paddingLeft: '4px' }}>
                    <span style={{ fontWeight: '600' }}>{aantal}x {soort}</span>
                    {omschr ? ` — ${omschr}` : ''}
                    {gewicht ? <span style={{ color: '#888' }}> · {gewicht} kg</span> : ''}
                    {afm ? <span style={{ color: '#888' }}> · {afm}</span> : ''}
                  </div>
                );
              })}
            </>
          )}

        </div>
      )}
    </div>
  );
};

// Live klok component
const LiveKlok = () => {
  const [nu, setNu] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setNu(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  const dag = nu.toLocaleDateString('nl-NL', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  const tijd = nu.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  return (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'flex-end',
      background: 'rgba(26,54,76,0.92)', borderRadius: '8px',
      padding: '6px 14px', backdropFilter: 'blur(4px)',
    }}>
      <span style={{ fontSize: '22px', fontWeight: '800', color: 'white', letterSpacing: '0.05em', lineHeight: 1.1, fontVariantNumeric: 'tabular-nums' }}>
        {tijd}
      </span>
      <span style={{ fontSize: '11px', color: 'rgba(194,230,239,0.9)', marginTop: '1px', textTransform: 'capitalize' }}>
        {dag}
      </span>
    </div>
  );
};
const CustomerTrackingMap = () => {
  const { t } = useLanguage();
  const [aanvragen, setAanvragen] = useState([]);
  const [trackerPositions, setTrackerPositions] = useState({});
  const [selectedTrackers, setSelectedTrackers] = useState(new Set());
  const [expandedTrackers, setExpandedTrackers] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [selectedLosplaats, setSelectedLosplaats] = useState('');
  const [zendingKleuren, setZendingKleuren] = useState(loadSavedColors);
  const [routeCoords, setRouteCoords] = useState([]);
  const [lastRefresh, setLastRefresh] = useState(null);

  const mapCenter = [52.1326, 5.2913];
  const mapZoom = 7;

  useEffect(() => {
    loadData();
    var interval = setInterval(loadData, 60000);

    // Herlaad data als gebruiker terugkomt naar dit tabblad
    var handleVisibility = function() {
      if (document.visibilityState === 'visible') {
        loadData();
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);

    return function() {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isFirstLoad = useRef(true);
  const mapRef = useRef(null);
  const manueelDeselected = useRef(new Set()); // trackers die gebruiker bewust uitgevinkt heeft

  const loadData = async () => {
    try {
      var response = await fetch('/api/customer/tracking-data', { credentials: 'include' });
      var data = await response.json();

      if (data.success) {
        var vandaag = new Date(); vandaag.setHours(0, 0, 0, 0);
        var activeAanvragen = (data.aanvragen || []).filter(function(a) {
          if (!a.LaadDatum) return true;
          var ld = new Date(a.LaadDatum); ld.setHours(0, 0, 0, 0);
          return ld <= vandaag;
        });
        setAanvragen(activeAanvragen);

        // Posities laden + GPS-coördinaten opslaan voor zoom
        
        for (var idx = 0; idx < activeAanvragen.length; idx++) {
          var aanvraag = activeAanvragen[idx];
          if (aanvraag.TrackerID) {
            await loadTrackerPosition(aanvraag.TrackerID, aanvraag.LaadDatum);
          }
        }

        // Selectie: nieuwe zendingen automatisch aanvinken, bestaande keuze bewaren
        if (isFirstLoad.current) {
          // Eerste keer: alles aanvinken
          var newSelected = new Set();
          for (var idx2 = 0; idx2 < activeAanvragen.length; idx2++) {
            if (activeAanvragen[idx2].TrackerID) {
              newSelected.add(activeAanvragen[idx2].AanvraagID);
            }
          }
          setSelectedTrackers(newSelected);
          isFirstLoad.current = false;
        } else {
          // Volgende refreshes: alleen NIEUWE zendingen toevoegen, bestaande selectie EN bewuste deselecties bewaren
          setSelectedTrackers(function(prev) {
            var updated = new Set(prev);
            for (var idx3 = 0; idx3 < activeAanvragen.length; idx3++) {
              var a = activeAanvragen[idx3];
              // Alleen toevoegen als: heeft tracker, niet al aanwezig, en NIET bewust uitgevinkt
              if (a.TrackerID && !prev.has(a.AanvraagID) && !manueelDeselected.current.has(a.AanvraagID)) {
                updated.add(a.AanvraagID);
              }
            }
            return updated;
          });
        }

        setRouteCoords([]);
      }
    } catch (err) {
      console.error('Error loading tracking data:', err);
    } finally {
      setLoading(false);
      setLastRefresh(new Date());
    }
  };

  const loadTrackerPosition = async (trackerId, laadDatum) => {
    try {
      var url = '/api/customer/tracker-positions/' + trackerId + '?limit=5000';
      if (laadDatum) url += '&from=' + new Date(laadDatum).toISOString();
      var response = await fetch(url, { credentials: 'include' });
      var positions = await response.json();
      if (Array.isArray(positions) && positions.length > 0) {
        setTrackerPositions(function(prev) {
          var next = Object.assign({}, prev);
          var last = positions[positions.length - 1];
          // Normaliseer timestamp veld (gps_timestamp of timestamp)
          var normaliseer = function(p) {
            return Object.assign({}, p, { timestamp: p.gps_timestamp || p.timestamp || p.time || null });
          };
          next[trackerId] = {
            current: normaliseer(last),
            history: positions.map(function(p) { return [p.latitude, p.longitude]; }),
            fullHistory: positions.map(normaliseer)
          };
          return next;
        });
      }
    } catch (err) {
      console.error('Error loading positions for ' + trackerId + ':', err);
    }
  };
  // Variant die ook de ruwe posities teruggeeft voor zoom-berekening
  const loadTrackerPositionMetData = async (trackerId, laadDatum) => {
    try {
      var url = '/api/customer/tracker-positions/' + trackerId + '?limit=5000';
      if (laadDatum) url += '&from=' + new Date(laadDatum).toISOString();
      var response = await fetch(url, { credentials: 'include' });
      var positions = await response.json();
      if (Array.isArray(positions) && positions.length > 0) {
        // Ook de normale update doen
        await loadTrackerPosition(trackerId, laadDatum);
        return positions;
      }
      return [];
    } catch(e) { return []; }
  };


  const handleColorChange = useCallback((aanvraagId, color) => {
    setZendingKleuren(prev => {
      const updated = { ...prev, [aanvraagId]: color };
      saveColors(updated);
      return updated;
    });
  }, []);

  const toggleTracker = (aanvraagId) => {
    setSelectedTrackers(function(prev) {
      var n = new Set(prev);
      if (n.has(aanvraagId)) {
        n.delete(aanvraagId);
        manueelDeselected.current.add(aanvraagId); // onthoud bewuste deselectie
      } else {
        n.add(aanvraagId);
        manueelDeselected.current.delete(aanvraagId); // niet meer deselected
      }
      return n;
    });
  };

  const toggleExpand = (aanvraagId) => {
    setExpandedTrackers(function(prev) {
      var n = new Set(prev);
      if (n.has(aanvraagId)) n.delete(aanvraagId); else n.add(aanvraagId);
      return n;
    });
  };

  var losplaatsen = useMemo(function() {
    return [...new Set(aanvragen.map(function(a) { return a.LosPlaats; }).filter(Boolean))].sort();
  }, [aanvragen]);

  var filteredAanvragen = useMemo(function() {
    if (!selectedLosplaats) return aanvragen;
    return aanvragen.filter(function(a) { return a.LosPlaats === selectedLosplaats; });
  }, [aanvragen, selectedLosplaats]);

  if (loading) {
    return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#888' }}>{t('laden')}</div>;
  }

  if (aanvragen.length === 0) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#999' }}>
        <div style={{ fontSize: '40px', marginBottom: '10px' }}>📦</div>
        <p style={{ margin: 0, fontSize: '16px', fontWeight: '600' }}>{t('geen_actieve_zendingen')}</p>
        <p style={{ margin: '4px 0 0', fontSize: '13px' }}>{t('geen_actieve_zendingen_sub') || 'Er zijn momenteel geen zendingen onderweg.'}</p>
      </div>
    );
  }

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <style dangerouslySetInnerHTML={{ __html: '@media(max-width:768px){.sw-sb{display:none!important;position:absolute!important;top:0!important;left:0!important;right:0!important;bottom:0!important;width:100%!important;z-index:1000!important;background:rgba(249,250,251,.97)!important;border-left:none!important}.sw-sb.sw-open{display:flex!important}.sw-fab{display:flex!important}.sw-x{display:flex!important}}@media(min-width:769px){.sw-fab{display:none!important}.sw-x{display:none!important}}' }} />

      {/* Header */}
      <div style={{ padding: '8px 12px', borderBottom: '1px solid #e5e7eb', background: 'linear-gradient(to right, #1a364c, #2a4a64)', flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>

          {/* Links: Live Tracking label */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
            <MapPin size={16} color="#c2e6ef" />
            <span style={{ fontSize: '13px', fontWeight: '700', color: 'white' }}>{t('live_tracking')}</span>
            <span style={{ background: 'rgba(194,230,239,0.2)', color: '#c2e6ef', fontSize: '11px', fontWeight: '600', padding: '2px 8px', borderRadius: '10px', border: '1px solid rgba(194,230,239,0.3)' }}>
              {filteredAanvragen.length} {t('zendingen').toLowerCase()}
            </span>
          </div>

          {/* Midden: Actieve zendingen */}
          <div style={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '24px', fontWeight: '800', color: 'white' }}>
              {filteredAanvragen.filter(function(a) {
                var sl=(a.Status||'').toLowerCase();
                return sl.includes('onderweg')||sl.includes('opdracht verzonden')||sl.includes('opdracht ontvangen');
              }).length}
            </span>
            <span style={{ fontSize: '12px', color: 'rgba(194,230,239,0.85)', fontWeight: '500' }}>{t('dashboard_actieve_zendingen').toLowerCase()}</span>
          </div>

          {/* Rechts: Filter */}
          <div style={{ flex: 1, display: 'flex', justifyContent: 'flex-end' }}>
            {losplaatsen.length > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Filter size={13} color="#c2e6ef" />
                <select
                  value={selectedLosplaats}
                  onChange={e => setSelectedLosplaats(e.target.value)}
                  style={{ fontSize: '12px', border: '1px solid rgba(194,230,239,0.4)', borderRadius: '6px', padding: '3px 8px', background: 'white', color: '#1a364c' }}
                >
                  <option value="">{t('alle_bestemmingen')}</option>
                  {losplaatsen.map(p => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
            )}
          </div>

        </div>
      </div>

      {/* Map + Sidebar */}
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden', position: 'relative' }}>
        <div style={{ flex: 1, position: 'relative' }}>
          <MapContainer center={mapCenter} zoom={mapZoom} style={{ height: '100%', width: '100%' }} zoomControl zoomSnap={0.1} zoomDelta={0.5}>
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution='&copy; OpenStreetMap contributors'
            />
            <MapRefSetter mapRef={mapRef} />
            <AutoFitBounds
              trackerPositions={trackerPositions}
              selectedTrackers={selectedTrackers}
              aanvragen={aanvragen}
            />
            <InvalidateSize />

            <ResetControl
              trackerPositions={trackerPositions}
              selectedTrackers={selectedTrackers}
              filteredAanvragen={filteredAanvragen}
            />

            {filteredAanvragen.map(function(aanvraag) {
              if (!aanvraag.TrackerID) return null;
              if (!selectedTrackers.has(aanvraag.AanvraagID)) return null;
              var position = trackerPositions[aanvraag.TrackerID];
              if (!position) return null;
              var current = position.current;
              if (!current) return null;

              var lineColor = zendingKleuren[aanvraag.AanvraagID] || getDefaultColorForId(aanvraag.AanvraagID);
              var late = isLate(aanvraag.LosDatum);
              var gepland = aanvraag.LosDatum ? new Date(aanvraag.LosDatum) : null;
              if (gepland) gepland.setHours(0, 0, 0, 0);

              // Goederen omschrijving samenvatten
              var goederenTekst = '-';
              if (aanvraag.Goederen && aanvraag.Goederen.length > 0) {
                goederenTekst = aanvraag.Goederen.map(function(g) {
                  var delen = [];
                  if (g.Aantal) delen.push(g.Aantal + 'x');
                  if (g.Soort) delen.push(g.Soort);
                  if (g.Omschrijving) delen.push(g.Omschrijving);
                  return delen.join(' ');
                }).filter(Boolean).join(', ');
              }

              return (
                <React.Fragment key={aanvraag.AanvraagID}>
                  {position.history && position.history.length > 1 && (
                    <Polyline positions={position.history} color={lineColor} weight={4} opacity={0.8} />
                  )}
                  {position.fullHistory && position.fullHistory.map(function(pos, idx) {
                    return (
                      <CircleMarker
                        key={idx}
                        center={[pos.latitude, pos.longitude]}
                        radius={3}
                        pathOptions={{ color: 'white', fillColor: lineColor, fillOpacity: 1, weight: 1.5, opacity: 1 }}
                        eventHandlers={{
                          mouseover: function(e) { e.target.setStyle({ radius: 6, weight: 2 }); e.target.openTooltip(); },
                          mouseout: function(e) { e.target.setStyle({ radius: 3, weight: 1.5 }); e.target.closeTooltip(); }
                        }}
                      >
                        <Tooltip direction="top" offset={[0, -8]}>
                          <RouteTooltipContent pos={pos} />
                        </Tooltip>
                      </CircleMarker>
                    );
                  })}
                  <Marker position={[current.latitude, current.longitude]} icon={createColoredIcon(lineColor)}>
                    <Popup>
                      <div style={{ minWidth: '240px' }}>
                        <strong style={{ fontSize: '15px', color: '#1a364c' }}>{aanvraag.AanvraagID}</strong>
                        <hr style={{ margin: '6px 0', border: 'none', borderTop: '1px solid #eee' }} />
                        <div style={{ fontSize: '13px', lineHeight: '1.7' }}>
                          <div><strong>{t('laadplaats')}:</strong> {[aanvraag.LaadPlaats, aanvraag.LaadLand].filter(Boolean).join(', ')}</div>
                          <div><strong>{t('losplaats')}:</strong> {[aanvraag.LosPlaats, aanvraag.LosLand].filter(Boolean).join(', ')}</div>
                          <div><strong>{t('goederen')}:</strong> {goederenTekst}</div>
                          <hr style={{ margin: '6px 0', border: 'none', borderTop: '1px dashed #ddd' }} />
                          <div style={{ color: late ? '#dc2626' : '#374151', fontWeight: late ? '700' : '400' }}>
                            <strong>{t('losdatum')}:</strong> {gepland ? gepland.toLocaleDateString('nl-NL') : '-'}
                            {late ? ' ⚠️ Te laat' : ''}
                          </div>
                        </div>
                      </div>
                    </Popup>
                  </Marker>
                </React.Fragment>
              );
            })}
          </MapContainer>
        </div>

        {/* RIGHT SIDEBAR */}
        <div className="sw-sb" id="swSB" style={{ width: '300px', background: '#f9fafb', borderLeft: '1px solid #e5e7eb', display: 'flex', flexDirection: 'column', flexShrink: 0 }}>
          <div style={{ padding: '10px 12px', borderBottom: '1px solid #e5e7eb', background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <div style={{ fontSize: '13px', fontWeight: '700', color: '#1f2937' }}>{t('zendingen')}</div>
              <div style={{ fontSize: '11px', color: '#888' }}>{t('klik_op_kaart')}</div>
            </div>
            <button className="sw-x" onClick={function(){document.getElementById('swSB').classList.remove('sw-open')}}
              style={{background:'#ef4444',color:'#fff',border:'none',borderRadius:'8px',width:'36px',height:'36px',fontSize:'18px',cursor:'pointer',display:'none',alignItems:'center',justifyContent:'center'}}
            >{'\u2715'}</button>
          </div>

          <div style={{ flex: 1, overflowY: 'auto', padding: '10px' }}>
            {filteredAanvragen.map(function(aanvraag) {
              if (!aanvraag.TrackerID) return null;
              var position = trackerPositions[aanvraag.TrackerID];
              var lineColor = zendingKleuren[aanvraag.AanvraagID] || getDefaultColorForId(aanvraag.AanvraagID);
              return (
                <TrackerCard
                  key={aanvraag.AanvraagID}
                  aanvraag={aanvraag}
                  position={position}
                  isSelected={selectedTrackers.has(aanvraag.AanvraagID)}
                  isExpanded={expandedTrackers.has(aanvraag.AanvraagID)}
                  onToggleSelect={function() { toggleTracker(aanvraag.AanvraagID); }}
                  onToggleExpand={function() { toggleExpand(aanvraag.AanvraagID); }}
                  lineColor={lineColor}
                  onColorChange={handleColorChange}
                />
              );
            })}
          </div>
        </div>
      </div>
      <button className="sw-fab" onClick={function(){document.getElementById('swSB').classList.toggle('sw-open')}}
        style={{position:'absolute',bottom:'20px',right:'20px',zIndex:999,background:'#7c3aed',color:'#fff',border:'none',borderRadius:'50%',width:'56px',height:'56px',fontSize:'24px',cursor:'pointer',boxShadow:'0 4px 15px rgba(124,58,237,0.4)',display:'none',alignItems:'center',justifyContent:'center'}}
      >{'\ud83d\udce6'}</button>
    </div>
  );
};

export default CustomerTrackingMap;
